import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  Activity,
  BedDouble,
  CalendarClock,
  CalendarX,
  ChevronRight,
  Clock3,
  CreditCard,
  FlaskConical,
  Gauge,
  HeartPulse,
  Hourglass,
  Landmark,
  ListChecks,
  PackageX,
  Repeat,
  ScanLine,
  Siren,
  Stethoscope,
  Timer,
  TrendingUp,
  UserPlus,
  Users,
  Wallet,
  type LucideIcon,
} from 'lucide-react';
import { PATIENT_BEARING_MODULES, type DashboardOverviewDto, type ModuleCode } from '@his/shared';
import { useAuth } from '@/lib/auth';
import { tr } from '@/lib/i18n-core';
import { cn, humanize, money, moneyCompact } from '@/lib/utils';
import {
  activeHourWindow,
  busiestHour,
  occupancyLevel,
  queueStages,
  sumBy,
  type QueueStageKey,
} from './dashboard-math';
import {
  DonutChart,
  hourLabel,
  HourlyChart,
  PairedBarChart,
  RadialGauge,
  Sparkline,
  TrendChart,
  type Series,
} from './dashboard-charts';
import {
  BarList,
  EmptyChart,
  formatCount,
  IconBadge,
  KpiCard,
  moneyWhole,
  Panel,
  PanelLink,
  TONE_COLOR,
  useMounted,
  type Tone,
} from './dashboard-ui';

const STAGE_COLOR: Record<QueueStageKey, string> = {
  expected: TONE_COLOR.sky,
  waiting: TONE_COLOR.amber,
  with_doctor: TONE_COLOR.violet,
  seen: TONE_COLOR.green,
  no_show: TONE_COLOR.rose,
  cancelled: 'hsl(var(--muted-foreground))',
};

const MODE_COLORS = [
  TONE_COLOR.primary,
  TONE_COLOR.sky,
  TONE_COLOR.violet,
  TONE_COLOR.amber,
  TONE_COLOR.rose,
  TONE_COLOR.green,
];

const TRIAGE_STYLE: Record<string, { label?: string; className: string }> = {
  level_1: { label: 'L1', className: 'bg-destructive/15 text-destructive' },
  level_2: { label: 'L2', className: 'bg-chart-4/15 text-chart-4' },
  level_3: { label: 'L3', className: 'bg-warning/15 text-warning' },
  level_4: { label: 'L4', className: 'bg-success/15 text-success' },
  level_5: { label: 'L5', className: 'bg-chart-2/15 text-chart-2' },
  untriaged: { className: 'bg-muted text-muted-foreground' },
};

function minutesText(minutes: number): string {
  if (minutes <= 0) return '—';
  if (minutes < 60) return tr('admin', 'dashboard_minutes', { minutes: Math.round(minutes) });
  const hours = Math.floor(minutes / 60);
  const rest = Math.round(minutes % 60);
  return rest ? tr('admin', 'dashboard_hours_minutes', { hours, minutes: rest }) : tr('admin', 'dashboard_hours', { hours });
}

function lastWeekCaption(count: number): string {
  return tr('admin', 'dashboard_last_week_by_now', { count: formatCount(count) });
}

interface KpiSpec {
  key: string;
  works: boolean;
  node: (index: number) => ReactNode;
}

export function HospitalOverview({
  data,
  currentHour,
}: {
  data: DashboardOverviewDto;
  currentHour: number;
}) {
  const { worksIn } = useAuth();
  const has = (module: ModuleCode) => worksIn(module);
  const anyPatients = PATIENT_BEARING_MODULES.some(worksIn);
  const days = data.days;
  const occupancy = data.beds.total ? Math.round((data.beds.occupied / data.beds.total) * 100) : 0;
  const stages = queueStages(data.appointments);
  const waiting = stages.find((stage) => stage.key === 'waiting')?.count ?? 0;

  const kpis: KpiSpec[] = [
    {
      key: 'opd',
      works: has('opd'),
      node: (index) => (
        <KpiCard
          index={index}
          label={tr('admin', 'dashboard_opd_visits_today')}
          icon={Stethoscope}
          tone="primary"
          value={data.today.opdVisits.today}
          soFar={data.today.opdVisits}
          caption={lastWeekCaption(data.today.opdVisits.lastWeek)}
          chart={<Sparkline data={days.map((day) => day.opdVisits)} color={TONE_COLOR.primary} />}
          to="/queue"
        />
      ),
    },
    {
      key: 'patients',
      works: anyPatients,
      node: (index) => (
        <KpiCard
          index={index}
          label={tr('admin', 'dashboard_new_patients')}
          icon={UserPlus}
          tone="sky"
          value={data.today.newPatients.today}
          soFar={data.today.newPatients}
          caption={lastWeekCaption(data.today.newPatients.lastWeek)}
          chart={<Sparkline data={days.map((day) => day.newPatients)} color={TONE_COLOR.sky} />}
          to="/patients"
        />
      ),
    },
    {
      key: 'billing',
      works: has('billing'),
      node: (index) => (
        <KpiCard
          index={index}
          label={tr('admin', 'dashboard_collected_today')}
          icon={Wallet}
          tone="green"
          value={data.today.collectedMinor.today}
          format={moneyWhole}
          soFar={data.today.collectedMinor}
          caption={tr('admin', 'dashboard_billed_today', { amount: moneyCompact(data.today.billedMinor.today) })}
          chart={
            <Sparkline data={days.map((day) => day.collectedMinor)} color={TONE_COLOR.green} />
          }
          to="/billing"
        />
      ),
    },
    {
      key: 'ipd',
      works: has('ipd'),
      node: (index) => (
        <KpiCard
          index={index}
          label={tr('admin', 'dashboard_inpatients_now')}
          icon={BedDouble}
          tone="violet"
          value={data.beds.occupied}
          caption={tr('admin', 'dashboard_admitted_discharged_today', {
            admitted: data.today.admissions,
            discharged: data.today.discharges,
          })}
          chart={<Sparkline data={days.map((day) => day.admissions)} color={TONE_COLOR.violet} />}
          to="/ipd/beds"
        />
      ),
    },
    {
      key: 'lab',
      works: has('lab'),
      node: (index) => (
        <KpiCard
          index={index}
          label={tr('admin', 'dashboard_lab_tests_pending')}
          icon={FlaskConical}
          tone="amber"
          value={data.pending.labs}
          caption={tr('admin', 'dashboard_ordered_not_verified')}
          to="/lab"
        />
      ),
    },
    {
      key: 'er',
      works: has('er'),
      node: (index) => (
        <KpiCard
          index={index}
          label={tr('admin', 'dashboard_active_er_cases')}
          icon={Siren}
          tone="rose"
          value={data.er.active}
          caption={tr('admin', 'dashboard_awaiting_disposition')}
          to="/er"
        />
      ),
    },
  ];
  const shownKpis = kpis.filter((kpi) => kpi.works).slice(0, 4);

  const flowSeries: (Series & { works: boolean })[] = [
    { key: 'opdVisits', label: tr('admin', 'dashboard_opd_visits'), color: TONE_COLOR.primary, works: has('opd') },
    { key: 'newPatients', label: tr('admin', 'dashboard_new_patients'), color: TONE_COLOR.sky, works: anyPatients },
    { key: 'admissions', label: tr('admin', 'dashboard_admissions'), color: TONE_COLOR.violet, works: has('ipd') },
    { key: 'discharges', label: tr('admin', 'dashboard_discharges'), color: TONE_COLOR.amber, works: has('ipd') },
  ];
  const shownFlow = flowSeries.filter((series) => series.works);
  const flowTotals = Object.fromEntries(
    shownFlow.map((series) => [
      series.key,
      formatCount(sumBy(days, (day) => Number(day[series.key as keyof typeof day]) || 0)),
    ]),
  );

  const billed14 = sumBy(days, (day) => day.billedMinor);
  const collected14 = sumBy(days, (day) => day.collectedMinor);
  const collectionRate = billed14 > 0 ? Math.round((collected14 / billed14) * 100) : null;
  const peak = busiestHour(data.opdByHour);
  const level = occupancyLevel(occupancy);

  let panelIndex = shownKpis.length;
  const next = () => panelIndex++;

  return (
    <div className="space-y-4">
      {shownKpis.length > 0 && (
        <div
          className={cn(
            'grid grid-cols-2 gap-3 sm:gap-4',
            shownKpis.length >= 4
              ? 'xl:grid-cols-4'
              : shownKpis.length === 3
                ? 'xl:grid-cols-3'
                : '',
          )}
        >
          {shownKpis.map((kpi, index) => (
            <div key={kpi.key} className="min-w-0">
              {kpi.node(index)}
            </div>
          ))}
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-3">
        {shownFlow.length > 0 && (
          <Panel
            index={next()}
            className={has('opd') ? 'lg:col-span-2' : 'lg:col-span-3'}
            title={tr('admin', 'dashboard_patient_flow')}
            subtitle={tr('admin', 'dashboard_patient_flow_hint')}
            icon={Activity}
          >
            <TrendChart data={days} series={shownFlow} totals={flowTotals} />
          </Panel>
        )}

        {has('opd') && (
          <Panel
            index={next()}
            title={tr('admin', 'dashboard_todays_appointments')}
            subtitle={tr('admin', 'dashboard_todays_appointments_hint')}
            icon={ListChecks}
            tone="sky"
            action={<PanelLink to="/queue">{tr('admin', 'dashboard_queue')}</PanelLink>}
          >
            <DonutChart
              slices={stages.map((stage) => ({
                key: stage.key,
                label: tr('admin', `dashboard_stage_${stage.key}`),
                value: stage.count,
                color: STAGE_COLOR[stage.key],
              }))}
              centerLabel={tr('admin', 'dashboard_booked_today')}
              empty={tr('admin', 'dashboard_no_appointments_today')}
            />
          </Panel>
        )}
      </div>

      {(has('opd') || has('ipd')) && (
        <div className="grid gap-4 lg:grid-cols-3">
          {has('opd') && (
            <Panel
              index={next()}
              className={has('ipd') ? 'lg:col-span-2' : 'lg:col-span-3'}
              title={tr('admin', 'dashboard_opd_by_hour')}
              subtitle={
                peak
                  ? tr('admin', 'dashboard_opd_by_hour_peak', { hour: hourLabel(peak.hour) })
                  : tr('admin', 'dashboard_opd_by_hour_hint')
              }
              icon={Clock3}
              tone="amber"
            >
              <HourlyChart
                data={activeHourWindow(data.opdByHour, currentHour)}
                currentHour={currentHour}
              />
            </Panel>
          )}

          {has('ipd') && (
            <Panel
              index={next()}
              className={has('opd') ? '' : 'lg:col-span-3'}
              title={tr('admin', 'dashboard_bed_occupancy')}
              subtitle={
                data.beds.total
                  ? tr('admin', 'dashboard_beds_free_now', { count: data.beds.total - data.beds.occupied })
                  : tr('admin', 'dashboard_no_beds_yet')
              }
              icon={BedDouble}
              tone="violet"
              action={<PanelLink to="/ipd/beds">{tr('admin', 'dashboard_bed_board')}</PanelLink>}
            >
              {data.beds.total === 0 ? (
                <EmptyChart>{tr('admin', 'dashboard_add_wards_hint')}</EmptyChart>
              ) : (
                <>
                  <RadialGauge
                    pct={occupancy}
                    color={
                      level === 'full'
                        ? 'hsl(var(--destructive))'
                        : level === 'busy'
                          ? TONE_COLOR.amber
                          : TONE_COLOR.violet
                    }
                    label={tr(
                      'admin',
                      level === 'full'
                        ? 'dashboard_nearly_full'
                        : level === 'busy'
                          ? 'dashboard_filling_up'
                          : 'dashboard_room_to_admit',
                    )}
                    detail={tr('admin', 'dashboard_beds_of_total', { occupied: data.beds.occupied, total: data.beds.total })}
                  />
                  <WardMeters wards={data.beds.wards} />
                  <BedStatusChips byStatus={data.beds.byStatus} />
                </>
              )}
            </Panel>
          )}
        </div>
      )}

      {has('billing') && (
        <div className="grid gap-4 lg:grid-cols-3">
          <Panel
            index={next()}
            className="lg:col-span-2"
            title={tr('admin', 'dashboard_billed_and_collected')}
            subtitle={
              collectionRate === null
                ? tr('admin', 'dashboard_last_14_days')
                : tr('admin', 'dashboard_collected_against_billed', {
                    collected: moneyCompact(collected14),
                    billed: moneyCompact(billed14),
                    pct: collectionRate,
                  })
            }
            icon={TrendingUp}
            tone="green"
            action={<PanelLink to="/reports">{tr('admin', 'dashboard_reports')}</PanelLink>}
          >
            <PairedBarChart
              data={days}
              series={[
                { key: 'billedMinor', label: tr('admin', 'dashboard_billed'), color: TONE_COLOR.green },
                { key: 'collectedMinor', label: tr('admin', 'dashboard_collected'), color: TONE_COLOR.green },
              ]}
              valueFormatter={money}
              axisFormatter={moneyCompact}
            />
          </Panel>

          <Panel
            index={next()}
            title={tr('admin', 'dashboard_todays_collections')}
            subtitle={tr('admin', 'dashboard_todays_collections_hint')}
            icon={CreditCard}
            tone="primary"
          >
            <DonutChart
              slices={data.collectionsByMode.map((row, index) => ({
                key: row.mode,
                label: humanize(row.mode),
                value: Math.max(0, row.amountMinor),
                color: MODE_COLORS[index % MODE_COLORS.length],
              }))}
              centerLabel={tr('admin', 'dashboard_collected_center')}
              format={moneyCompact}
              empty={tr('admin', 'dashboard_nothing_collected')}
            />
          </Panel>
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {has('billing') && (
          <Panel
            index={next()}
            title={tr('admin', 'dashboard_revenue_by_department')}
            subtitle={tr('admin', 'dashboard_net_billed_30_days')}
            icon={Landmark}
            tone="green"
          >
            <BarList
              rows={data.month.departments.map((row) => ({
                label: row.department,
                value: row.netMinor,
              }))}
              format={moneyCompact}
              tone="green"
              empty={tr('admin', 'dashboard_no_billing_30_days')}
            />
          </Panel>
        )}

        {has('opd') && (
          <Panel
            index={next()}
            title={tr('admin', 'dashboard_top_diagnoses')}
            subtitle={tr('admin', 'dashboard_top_diagnoses_hint')}
            icon={HeartPulse}
            tone="rose"
          >
            <BarList
              rows={data.month.diagnoses.map((row) => ({
                label: row.icdTitle,
                value: row.count,
                hint: row.icdCode,
              }))}
              tone="rose"
              empty={tr('admin', 'dashboard_no_diagnoses_30_days')}
            />
          </Panel>
        )}

        <NeedsAttention data={data} waiting={waiting} index={next()} />
      </div>

      <QualityStrip data={data} index={next()} />
    </div>
  );
}

function WardMeters({ wards }: { wards: DashboardOverviewDto['beds']['wards'] }) {
  const mounted = useMounted();
  if (wards.length <= 1) return null;
  return (
    <ul className="mt-4 space-y-2.5">
      {wards.slice(0, 6).map((ward, index) => {
        const pct = ward.total ? Math.round((ward.occupied / ward.total) * 100) : 0;
        const level = occupancyLevel(pct);
        return (
          <li key={ward.ward}>
            <div className="mb-1 flex items-baseline justify-between gap-2 text-xs">
              <span className="min-w-0 truncate font-medium">{ward.ward}</span>
              <span className="shrink-0 tabular-nums text-muted-foreground">
                {ward.occupied}/{ward.total}
              </span>
            </div>
            <div className="h-1.5 overflow-hidden rounded-full bg-muted">
              <div
                className={cn(
                  'h-full rounded-full transition-[width] duration-700 ease-out',
                  level === 'full'
                    ? 'bg-destructive'
                    : level === 'busy'
                      ? 'bg-chart-4'
                      : 'bg-chart-3',
                )}
                style={{
                  width: mounted ? `${Math.max(pct, 2)}%` : '0%',
                  transitionDelay: `${index * 70}ms`,
                }}
              />
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function BedStatusChips({ byStatus }: { byStatus: DashboardOverviewDto['beds']['byStatus'] }) {
  const others = byStatus.filter(
    (row) => row.key !== 'occupied' && row.key !== 'vacant' && row.count > 0,
  );
  if (others.length === 0) return null;
  return (
    <div className="mt-4 flex flex-wrap gap-1.5">
      {others.map((row) => (
        <span
          key={row.key}
          className="rounded-full bg-muted px-2 py-0.5 text-[11px] text-muted-foreground"
        >
          {humanize(row.key)} <span className="font-semibold text-foreground">{row.count}</span>
        </span>
      ))}
    </div>
  );
}

interface AttentionRow {
  key: string;
  icon: LucideIcon;
  tone: Tone;
  label: string;
  detail: ReactNode;
  count: number;
  to: string;
}

function NeedsAttention({
  data,
  waiting,
  index,
}: {
  data: DashboardOverviewDto;
  waiting: number;
  index: number;
}) {
  const { worksIn } = useAuth();
  const rows: AttentionRow[] = [];
  if (worksIn('opd')) {
    rows.push({
      key: 'waiting',
      icon: Hourglass,
      tone: 'amber',
      label: tr('admin', 'dashboard_patients_waiting'),
      detail: tr('admin', 'dashboard_arrived_not_seen'),
      count: waiting,
      to: '/queue',
    });
  }
  if (worksIn('er')) {
    rows.push({
      key: 'er',
      icon: Siren,
      tone: 'rose',
      label: tr('admin', 'dashboard_active_er_cases'),
      detail: data.er.byTriage.length ? (
        <TriageChips byTriage={data.er.byTriage} />
      ) : (
        tr('admin', 'dashboard_awaiting_disposition')
      ),
      count: data.er.active,
      to: '/er',
    });
  }
  if (worksIn('lab')) {
    rows.push({
      key: 'lab',
      icon: FlaskConical,
      tone: 'sky',
      label: tr('admin', 'dashboard_lab_tests_pending'),
      detail: tr('admin', 'dashboard_ordered_not_verified'),
      count: data.pending.labs,
      to: '/lab',
    });
  }
  if (worksIn('radiology')) {
    rows.push({
      key: 'radiology',
      icon: ScanLine,
      tone: 'violet',
      label: tr('admin', 'dashboard_imaging_pending'),
      detail: tr('admin', 'dashboard_ordered_not_reported'),
      count: data.pending.radiology,
      to: '/radiology',
    });
  }
  if (worksIn('pharmacy') || worksIn('inventory')) {
    rows.push({
      key: 'expiring',
      icon: PackageX,
      tone: 'amber',
      label: tr('admin', 'dashboard_batches_expiring'),
      detail: tr('admin', 'dashboard_batches_expiring_hint'),
      count: data.month.expiringBatches,
      to: '/inventory',
    });
  }
  if (rows.length === 0) return null;

  return (
    <Panel
      index={index}
      title={tr('admin', 'dashboard_needs_attention')}
      subtitle={tr('admin', 'dashboard_needs_attention_hint')}
      icon={CalendarClock}
      tone="amber"
    >
      <ul className="-mx-2 divide-y">
        {rows.map((row) => (
          <li key={row.key}>
            <Link
              to={row.to}
              className="group flex items-center gap-3 rounded-lg px-2 py-2.5 transition-colors hover:bg-accent/60 focus-ring"
            >
              <IconBadge icon={row.icon} tone={row.tone} className="h-8 w-8" />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium">{row.label}</span>
                <span className="block truncate text-xs text-muted-foreground">{row.detail}</span>
              </span>
              <span
                className={cn(
                  'min-w-[2rem] rounded-full px-2 py-0.5 text-center text-sm font-semibold tabular-nums',
                  row.count > 0 ? 'bg-foreground/5' : 'text-success',
                )}
              >
                {row.count > 0 ? formatCount(row.count) : '✓'}
              </span>
              <ChevronRight
                className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5 rtl:rotate-180"
                aria-hidden
              />
            </Link>
          </li>
        ))}
      </ul>
    </Panel>
  );
}

function TriageChips({ byTriage }: { byTriage: DashboardOverviewDto['er']['byTriage'] }) {
  const ordered = [...byTriage].sort((a, b) => a.key.localeCompare(b.key));
  return (
    <span className="inline-flex flex-wrap gap-1">
      {ordered.map((row) => {
        const style = TRIAGE_STYLE[row.key] ?? TRIAGE_STYLE.untriaged;
        return (
          <span
            key={row.key}
            className={cn('rounded px-1.5 text-[10px] font-semibold', style.className)}
          >
            {style.label ?? tr('admin', 'dashboard_untriaged')} · {row.count}
          </span>
        );
      })}
    </span>
  );
}

interface QualityTile {
  key: string;
  icon: LucideIcon;
  tone: Tone;
  label: string;
  value: string;
  hint: string;
}

function QualityStrip({ data, index }: { data: DashboardOverviewDto; index: number }) {
  const { worksIn } = useAuth();
  const kpis = data.month.kpis;
  const tiles: QualityTile[] = [];
  if (worksIn('lab')) {
    tiles.push({
      key: 'lab',
      icon: Timer,
      tone: 'sky',
      label: tr('admin', 'dashboard_lab_turnaround'),
      value: minutesText(kpis.labTurnaroundMinutes),
      hint: tr('admin', 'dashboard_lab_turnaround_hint'),
    });
  }
  if (worksIn('er')) {
    tiles.push({
      key: 'er',
      icon: Siren,
      tone: 'rose',
      label: tr('admin', 'dashboard_er_door_to_doctor'),
      value: minutesText(kpis.erWaitMinutes),
      hint: tr('admin', 'dashboard_er_door_to_doctor_hint'),
    });
  }
  if (worksIn('opd')) {
    tiles.push({
      key: 'no-show',
      icon: CalendarX,
      tone: 'amber',
      label: tr('admin', 'dashboard_no_show_rate'),
      value: `${kpis.noShowRatePct}%`,
      hint: tr('admin', 'dashboard_no_show_rate_hint'),
    });
  }
  if (worksIn('ipd')) {
    tiles.push({
      key: 'readmission',
      icon: Repeat,
      tone: 'violet',
      label: tr('admin', 'dashboard_readmissions'),
      value: `${kpis.readmissionRatePct}%`,
      hint: tr('admin', 'dashboard_readmissions_hint'),
    });
    tiles.push({
      key: 'alos',
      icon: Gauge,
      tone: 'green',
      label: tr('admin', 'dashboard_average_stay'),
      value: kpis.averageLengthOfStayDays ? tr('admin', 'dashboard_days', { count: kpis.averageLengthOfStayDays }) : '—',
      hint: tr('admin', 'dashboard_average_stay_hint'),
    });
  }
  if (tiles.length === 0) return null;

  return (
    <Panel
      index={index}
      title={tr('admin', 'dashboard_quality')}
      subtitle={tr('admin', 'dashboard_quality_hint')}
      icon={Users}
      tone="primary"
    >
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
        {tiles.map((tile) => (
          <div
            key={tile.key}
            className="flex items-start gap-3 rounded-lg border bg-background/50 p-3"
          >
            <IconBadge icon={tile.icon} tone={tile.tone} className="h-8 w-8" />
            <div className="min-w-0">
              <p className="text-xs text-muted-foreground">{tile.label}</p>
              <p className="text-lg font-semibold tabular-nums tracking-tight">{tile.value}</p>
              <p className="text-[11px] leading-snug text-muted-foreground">{tile.hint}</p>
            </div>
          </div>
        ))}
      </div>
    </Panel>
  );
}
